<script lang="ts">
  import { Toaster } from 'svelte-sonner';
  import { toast } from "svelte-sonner";
  import { usePage } from '@inertiajs/svelte';
  import { SidebarProvider, SidebarTrigger } from '$lib/components/ui/sidebar/index.js';
  import AppSidebar from '$lib/components/AppSidebar.svelte';
  import * as DropdownMenu from "$lib/components/ui/dropdown-menu";
  import { Avatar, AvatarFallback, AvatarImage } from "$lib/components/ui/avatar";
  import { router } from '@inertiajs/svelte';
  import { LogOut, User, Settings, Globe, } from "lucide-svelte";
  import { appStore } from '$lib/stores/appStore';

  let { title, children } = $props();
  let open = $state(true);

  const page = usePage();
  const user = $derived(page.props.auth?.user);

  const displayTitle = $derived(
    title || 
    $appStore.pageTitle || 
    (page.props as any).title || 
    t.default_title
  );

  let currentLocale = $derived((page.props as any).current_locale || 'zh-CN');
  let availableLocales = $derived((page.props as any).available_locales || [
    { code: 'zh-CN', label: '简体中文' },
    { code: 'en', label: 'English' }
  ]);


  function switchLanguage(locale: string) {
    if (locale === currentLocale) return;

    const toastId = toast.loading(t.switching_language || "Switching language...");

    // 请求后端接口修改 Locale
    router.post('/locale/switch', { locale }, {
      preserveState: false,
      onSuccess: () => toast.dismiss(toastId),
      onError: () => toast.dismiss(toastId),
    });
  }


  const translations = $derived((page.props as any).translations || {});
  const t = $derived(translations.sidebar || {});
  const tapp = $derived(translations.app || {});

  function handleLogout() {
    router.delete('/logout');
  }

  let lastHandledNotice = $state<string | null>(null);
  let lastHandledAlert = $state<string | null>(null);

  $effect(() => {
    const flash = page.props?.flash as any;

    if (flash?.notice && flash.notice !== lastHandledNotice) {
      toast.success(flash.notice);
      lastHandledNotice = flash.notice;
    }

    if (flash?.alert && flash.alert !== lastHandledAlert) {
      toast.error(flash.alert);
      lastHandledAlert = flash.alert;
    }
  });
</script>

<svelte:head>
  <title>{displayTitle} - {tapp.brand_suffix}</title>
</svelte:head>

<SidebarProvider bind:open>
  <div class="flex h-screen w-full overflow-hidden bg-background">
    <AppSidebar />

    <div class="flex-1 flex flex-col overflow-hidden">
      <header class="h-14 border-b flex items-center px-6 gap-4 bg-card shrink-0">
        <SidebarTrigger />
        <h1 class="text-xl font-semibold">{displayTitle}</h1>
        
        <div class="flex-1"></div>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger class="focus-visible:outline-none">
            <div class="flex items-center gap-1.5 hover:bg-accent p-2 rounded-md text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
              <Globe class="size-4 shrink-0" />
              <span class="text-xs font-medium hidden sm:inline-block">
                {availableLocales.find((l: any) => l.code === currentLocale)?.label || 'Language'}
              </span>
            </div>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="end" class="w-36 mt-1">
            <DropdownMenu.Label class="text-xs text-muted-foreground">{t.select_language || "Language"}</DropdownMenu.Label>
            <DropdownMenu.Separator />
            {#each availableLocales as lang}
              <DropdownMenu.Item
                class="gap-2 p-2 cursor-pointer text-xs justify-between"
                onclick={() => switchLanguage(lang.code)}
              >
                <span class={lang.code === currentLocale ? 'font-bold text-primary' : ''}>
                  {lang.label}
                </span>
                {#if lang.code === currentLocale}
                  <div class="size-1.5 rounded-full bg-primary"></div>
                {/if}
              </DropdownMenu.Item>
            {/each}
          </DropdownMenu.Content>
        </DropdownMenu.Root>

        {#if user}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger class="focus-visible:outline-none">
              <div class="flex items-center gap-3 hover:bg-accent p-1.5 rounded-full transition-colors cursor-pointer">
                <div class="text-right hidden sm:block">
                  <p class="text-xs font-medium leading-none">{user.name || t.anonymous_user}</p>
                  <p class="text-[10px] text-muted-foreground leading-tight mt-1">{user.email_address}</p>
                </div>

                <Avatar class="h-8 w-8 border shadow-sm">
                  <AvatarImage src={user.avatar_url}  alt={user.name} />
                  <AvatarFallback class="bg-primary/10 text-primary text-xs uppercase">
                    {(user.name || user.email_address).substring(0, 2)}
                  </AvatarFallback>
                </Avatar>
              </div>
            </DropdownMenu.Trigger>
            
            <DropdownMenu.Content align="end" class="w-56 mt-2">
              <DropdownMenu.Label class="font-normal">
                <div class="flex flex-col space-y-1">
                  <p class="text-sm font-medium leading-none">{user.name || t.anonymous_user}</p>
                  <p class="text-xs leading-none text-muted-foreground">{user.email_address}</p>
                </div>
              </DropdownMenu.Label>
              <DropdownMenu.Separator />
              <DropdownMenu.Group>
                <DropdownMenu.Item onclick={() => router.get('/profile')}>
                  <User class="mr-2 h-4 w-4" />
                  {t.profile}
                </DropdownMenu.Item>
                <DropdownMenu.Item>
                  <Settings class="mr-2 h-4 w-4" />
                  {t.preferences}
                </DropdownMenu.Item>
              </DropdownMenu.Group>
              <DropdownMenu.Separator />
              <DropdownMenu.Item onSelect={handleLogout} class="text-destructive focus:bg-destructive/10 focus:text-destructive">
                <LogOut class="mr-2 h-4 w-4" />
                {t.logout}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        {:else}
          <a href="/login" class="text-sm font-medium hover:underline">{t.login_btn}</a>
        {/if}
      </header>

      <main class="flex-1 overflow-auto p-6 bg-zinc-50/30">
        {@render children?.()}
      </main>
    </div>
  </div>
</SidebarProvider>
<Toaster position="top-center" richColors />